import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Trash2, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError, downloadFile, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CYCLE = ["full", "half", "absent", ""];
const LETTER = { full: "F", half: "H", absent: "A", "": "" };
const COLOUR = {
  full: "hsl(var(--healthy))",
  half: "hsl(var(--watch))",
  absent: "hsl(var(--critical))",
};

/** The participants of one programme, and the mark against each of its days. */
export const ProgrammeSheet = ({ programmeId, onClose, onChanged }) => {
  const { shows } = useAuth();
  const picker = useRef(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [fresh, setFresh] = useState({ name: "", email: "", department: "", designation: "" });

  const load = () => {
    if (!programmeId) return;
    api
      .get(`/training/programmes/${programmeId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(load, [programmeId]);

  const programme = data?.programme;
  const days = programme?.days || [];
  const people = data?.participants || [];
  const marks = data?.marks || {};
  const mayMark = !!programme?.can_edit && shows("training.attendance");
  const mayExcel = !!programme?.can_edit && shows("training.programme_excel");

  const mark = async (person, day) => {
    const now = person.attendance?.[day] || "";
    const next = CYCLE[(CYCLE.indexOf(now) + 1) % CYCLE.length];
    try {
      const { data: saved } = await api.put(`/training/programmes/${programmeId}/attendance`, {
        participant_id: person.id,
        date: day,
        mark: next || null,
      });
      setData((d) => ({
        ...d,
        participants: d.participants.map((p) => (p.id === saved.id ? saved : p)),
      }));
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addPerson = async () => {
    if (!fresh.name.trim()) {
      toast.error("The participant's name is needed.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/training/programmes/${programmeId}/participants`, fresh);
      setFresh({ name: "", email: "", department: "", designation: "" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removePerson = async (person) => {
    if (!window.confirm(`Take ${person.name} off this programme?`)) return;
    try {
      await api.delete(`/training/programmes/${programmeId}/participants/${person.id}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pullSheet = async () => {
    setBusy(true);
    try {
      await downloadFile(
        `/training/programmes/${programmeId}/template`,
        {},
        `${programme.plant}_${programme.kind}_participants.xlsx`,
      );
      toast.success("Fill the sheet and bring it back — nothing saves until you confirm.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const read = async (chosen) => {
    if (!chosen) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", chosen);
      const { data: said } = await api.post(
        `/training/programmes/${programmeId}/participants/import`,
        form,
      );
      setFile(chosen);
      setPreview(said.preview);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (picker.current) picker.current.value = "";
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: said } = await api.post(
        `/training/programmes/${programmeId}/participants/import?apply=true`,
        form,
      );
      toast.success(
        `${said.applied.added} added · ${said.applied.updated} corrected · ` +
          `${said.applied.marks} attendance mark(s) set.`,
      );
      setPreview(null);
      setFile(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Sheet open={!!programmeId} onOpenChange={(o) => !o && onClose?.()}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto border-l border-border bg-card sm:max-w-5xl"
          data-testid="programme-detail"
        >
          {!programme ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <SheetHeader>
                <div className="label-caps">{programme.kind}</div>
                <SheetTitle className="font-display text-2xl font-black tracking-tight">
                  {programme.title}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {programme.plant} · {programme.mode}
                  {programme.venue ? ` · ${programme.venue}` : ""} ·{" "}
                  {fmtDay(programme.from_date)} to {fmtDay(programme.to_date)} ·{" "}
                  {programme.day_count} day(s)
                  {programme.faculty ? ` · faculty ${programme.faculty}` : ""}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
                {[
                  ["Participants", programme.participants, "detail-participants"],
                  ["Training hours", programme.hours, "detail-hours"],
                  ["Man-days", programme.man_days, "detail-man-days"],
                  ["Days marked", `${programme.marked}`, "detail-marked"],
                ].map(([label, value, tid]) => (
                  <div key={label} className="bg-card p-3" data-testid={tid}>
                    <div className="label-caps">{label}</div>
                    <div className="font-data mt-1 text-xl font-bold">{value}</div>
                  </div>
                ))}
              </div>

              {programme.can_edit && (
                <div className="mt-5 border border-border p-4">
                  <div className="label-caps text-primary">The participant list</div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Add one by hand, or take the list out in Excel — Name, Mode, Venue, From and To,
                    with a column for every day of the programme — fill it and bring it back. Write
                    F for a full day (8 hours), H for half (4 hours) and A for absent.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Input
                      className="h-10 w-[190px] rounded-none"
                      placeholder="Name *"
                      data-testid="pp-name"
                      value={fresh.name}
                      onChange={(e) => setFresh({ ...fresh, name: e.target.value })}
                    />
                    <Input
                      className="h-10 w-[200px] rounded-none"
                      placeholder="officer@sail.in"
                      data-testid="pp-email"
                      value={fresh.email}
                      onChange={(e) => setFresh({ ...fresh, email: e.target.value })}
                    />
                    <Input
                      className="h-10 w-[160px] rounded-none"
                      placeholder="Department"
                      data-testid="pp-department"
                      value={fresh.department}
                      onChange={(e) => setFresh({ ...fresh, department: e.target.value })}
                    />
                    <Input
                      className="h-10 w-[150px] rounded-none"
                      placeholder="Designation"
                      data-testid="pp-designation"
                      value={fresh.designation}
                      onChange={(e) => setFresh({ ...fresh, designation: e.target.value })}
                    />
                    <Button
                      className="h-10 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
                      data-testid="pp-add"
                      disabled={busy}
                      onClick={addPerson}
                    >
                      <UserPlus className="mr-2 h-4 w-4" /> Add
                    </Button>
                    <Button
                      variant="outline"
                      className="h-10 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
                      data-testid="participants-download"
                      disabled={busy || !mayExcel}
                      onClick={pullSheet}
                    >
                      <Download className="mr-2 h-4 w-4" /> Excel
                    </Button>
                    <input
                      ref={picker}
                      type="file"
                      accept=".xlsx"
                      className="hidden"
                      data-testid="participants-file"
                      onChange={(e) => read(e.target.files?.[0])}
                    />
                    <Button
                      variant="outline"
                      className="h-10 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
                      data-testid="participants-upload"
                      disabled={busy || !mayExcel}
                      onClick={() => picker.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" /> Bring it back
                    </Button>
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px]">
                {Object.entries(marks).map(([key, label]) => (
                  <span
                    key={key}
                    className="flex items-center gap-1.5 border px-2 py-1"
                    style={{ borderColor: COLOUR[key] }}
                  >
                    <span className="h-2 w-2" style={{ backgroundColor: COLOUR[key] }} />
                    {label} · {LETTER[key]}
                  </span>
                ))}
                <span className="text-muted-foreground">
                  Press a cell to move it on — Full Day, Half Day, Absent, then blank.
                </span>
              </div>

              <div className="mt-3 overflow-x-auto border border-border">
                <table className="w-full text-xs" data-testid="attendance-grid">
                  <thead className="bg-muted">
                    <tr>
                      <th className="label-caps sticky left-0 z-10 bg-muted px-3 py-2 text-left">
                        Participant
                      </th>
                      {days.map((day) => (
                        <th key={day} className="label-caps px-1 py-2 text-center">
                          {day.slice(8)}.{day.slice(5, 7)}
                        </th>
                      ))}
                      <th className="label-caps px-3 py-2 text-right">Hours</th>
                      {programme.can_edit && <th className="px-2 py-2" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {people.map((person) => (
                      <tr key={person.id} data-testid={`participant-${person.id}`}>
                        <td className="sticky left-0 z-10 bg-card px-3 py-2">
                          <div className="font-semibold">{person.name}</div>
                          <div className="font-data text-[10px] text-muted-foreground">
                            {[person.email, person.department, person.mode]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </div>
                        </td>
                        {days.map((day) => {
                          const value = person.attendance?.[day] || "";
                          return (
                            <td key={day} className="px-0.5 py-1 text-center">
                              <button
                                type="button"
                                disabled={!mayMark}
                                data-testid={`att-${person.id}-${day}`}
                                data-state={value || "blank"}
                                onClick={() => mark(person, day)}
                                title={marks[value] || "Not marked"}
                                className="font-data h-7 w-7 border text-[11px] font-bold transition-colors duration-200 disabled:cursor-default"
                                style={{
                                  borderColor: value ? COLOUR[value] : "hsl(var(--border))",
                                  color: value ? COLOUR[value] : "hsl(var(--muted-foreground))",
                                  backgroundColor: value ? `${COLOUR[value]}1f` : "transparent",
                                }}
                              >
                                {LETTER[value] || "·"}
                              </button>
                            </td>
                          );
                        })}
                        <td
                          className="font-data px-3 py-2 text-right font-bold"
                          data-testid={`hours-${person.id}`}
                        >
                          {person.hours}
                        </td>
                        {programme.can_edit && (
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              data-testid={`participant-remove-${person.id}`}
                              onClick={() => removePerson(person)}
                              className="border border-border p-1 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {!people.length && (
                      <tr>
                        <td
                          colSpan={days.length + 2}
                          className="px-3 py-6 text-center text-muted-foreground"
                          data-testid="participants-empty"
                        >
                          Nobody on this programme yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!days.length && (
                <p className="mt-3 text-[11px] text-[hsl(var(--watch))]">
                  Give the programme a From and a To date and the day columns appear.
                </p>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto rounded-none bg-card sm:max-w-2xl"
          data-testid="participants-preview"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">
              This is what the sheet would change
            </DialogTitle>
            <DialogDescription className="text-xs">
              {file?.name} · {preview?.total} row(s) read. Nothing is saved until you press Save it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <p className="font-data">
              {preview?.added?.length || 0} new · {preview?.changed?.length || 0} corrected ·{" "}
              {preview?.unchanged || 0} unchanged · {preview?.mark_changes || 0} attendance
              mark(s)
            </p>
            {!!preview?.added?.length && (
              <p className="text-muted-foreground">
                New: {preview.added.map((a) => a.name).join(", ")}
              </p>
            )}
            {!!preview?.changed?.length && (
              <ul className="space-y-0.5">
                {preview.changed.slice(0, 12).map((c) => (
                  <li key={c.name} className="text-muted-foreground">
                    <b className="text-foreground">{c.name}</b> —{" "}
                    {[
                      ...c.fields.map((f) => `${f.label}: ${f.from || "blank"} → ${f.to}`),
                      c.marks ? `${c.marks} day(s) marked` : "",
                    ]
                      .filter(Boolean)
                      .join("; ")}
                  </li>
                ))}
              </ul>
            )}
            {!!preview?.not_in_sheet?.length && (
              <p className="text-muted-foreground">
                Not on the sheet, so left exactly as they are:{" "}
                {preview.not_in_sheet.join(", ")}
              </p>
            )}
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="h-11 rounded-none"
              data-testid="participants-cancel"
              onClick={() => setPreview(null)}
            >
              Leave it
            </Button>
            <Button
              className="h-11 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
              data-testid="participants-confirm"
              disabled={busy}
              onClick={apply}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
